import type {
  BrandSettings,
  FaviconValue,
  LogoValue,
  BrandAndThemeData,
} from '../types';
import { useQuery, registerQuery } from 'data-fetching-client';
import { getIsThemePreview, getPortalName } from '../utils/metadata';
import { matchByDomain } from '../utils/domainMatch';

export const FETCH_BRAND_DATA = registerQuery<
  'brandData',
  never,
  BrandAndThemeData
>({
  fieldName: 'brandData',
  fetcher: async () => {
    const portalName = getPortalName();
    if (!portalName) {
      throw new Error('Missing portalName');
    }
    const isThemePreviewer = getIsThemePreview();
    const basePath = `/hs/portals/${portalName}/brand`;
    const searchParams = new URLSearchParams(window.location.search);
    const previewToken = searchParams.get('hs_preview');
    const themeSettingsVersion = searchParams.get('theme_settings_version');
    let queryString = '';
    if (isThemePreviewer) {
      const brandParams = new URLSearchParams();
      if (previewToken) {
        brandParams.set('hs_preview', previewToken);
      }
      if (themeSettingsVersion) {
        brandParams.set('theme_settings_version', themeSettingsVersion);
      }
      queryString = brandParams.toString();
    }
    const requestUrl = queryString ? `${basePath}?${queryString}` : basePath;
    let res: Response;
    try {
      res = await fetch(requestUrl);
    } catch (e: unknown) {
      throw new Error('Failed to fetch brand data', { cause: e });
    }
    if (!res.ok) {
      console.error(`Brand data fetch failed: ${res.status} ${res.statusText}`);
      throw new Error('Failed to fetch brand data');
    }
    const data = await res.json();
    return data;
  },
});

export function useBrandData() {
  const { data } = useQuery(FETCH_BRAND_DATA);
  return data?.brandData;
}

export function useBrandSettings(): BrandSettings | undefined {
  const portalData = useBrandData();
  return portalData?.context?.brandSettings;
}

export function useFavicon(): FaviconValue | undefined | null {
  const brandSettings = useBrandSettings();
  return brandSettings?.primaryFavicon ?? brandSettings?.favicon;
}

export function useLogo(): LogoValue | undefined | null {
  const brandSettings = useBrandSettings();
  const domainMatch = matchByDomain(
    brandSettings?.logos ?? [],
    window.location.hostname
  );
  return domainMatch ?? brandSettings?.primaryLogo ?? brandSettings?.logo;
}

export function useBrandName(): string | undefined | null {
  const portalData = useBrandData();
  return portalData?.context?.brandName;
}

export function useCompanyName(): string | undefined | null {
  const portalData = useBrandData();
  return portalData?.context?.companyName;
}
